import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { ClipboardIcon, CalendarIcon, CheckCircleIcon, ClockIcon, XIcon, PaperAirplaneIcon, ExclamationCircleIcon } from './ui/icons';
import { getQuickAccessStatusEmailTemplate } from '../utils/emailTemplates';

interface TaskCategory {
  _id: string;
  name: string;
  color: string;
}

interface Task {
  _id: string;
  taskNumber: string;
  title: string;
  description: string;
  status: 'pending' | 'in_progress' | 'on_hold' | 'completed' | 'cancelled';
  priority: 'low' | 'medium' | 'high' | 'urgent';
  dueDate: string;
  createdAt: string;
  clientId?: {
    _id: string;
    name: string;
    company?: string;
    email?: string;
    phone?: string;
  } | null;
  categories?: TaskCategory[];
}

interface Meeting {
  _id: string;
  contact_name: string;
  company_name?: string;
  subject: string;
  date: string;
  start_time: string;
  end_time: string;
  status: 'pending' | 'confirmed' | 'completed' | 'cancelled' | 'rescheduled' | 'no_show';
  email?: string;
  mobile?: string;
  meetingType: 'online' | 'on-site';
  meeting_link?: string;
  meetingCategory?: 'support' | 'sales';
  customFields?: {
    linkedClientId?: string;
  };
}

interface TasksMeetingsQuickAccessProps {
  userId: string;
  isActive?: boolean;
}

const TASK_STATUS_OPTIONS = [
  { value: 'pending', label: 'Pending', color: 'bg-amber-100 text-amber-700' },
  { value: 'in_progress', label: 'In Progress', color: 'bg-blue-100 text-blue-700' },
  { value: 'on_hold', label: 'On Hold', color: 'bg-slate-100 text-slate-700' },
  { value: 'completed', label: 'Completed', color: 'bg-green-100 text-green-700' },
  { value: 'cancelled', label: 'Cancelled', color: 'bg-red-100 text-red-700' },
];

const MEETING_STATUS_OPTIONS = [
  { value: 'pending', label: 'Pending', color: 'bg-amber-100 text-amber-700' },
  { value: 'confirmed', label: 'Confirmed', color: 'bg-blue-100 text-blue-700' },
  { value: 'completed', label: 'Completed', color: 'bg-green-100 text-green-700' },
  { value: 'cancelled', label: 'Cancelled', color: 'bg-red-100 text-red-700' },
  { value: 'no_show', label: 'No Show', color: 'bg-slate-100 text-slate-700' },
  { value: 'waiting_for_recording', label: 'Waiting for Recording', color: 'bg-orange-100 text-orange-700' },
];

const PRIORITY_COLORS: Record<string, string> = {
  low: 'bg-slate-100 text-slate-600',
  medium: 'bg-amber-100 text-amber-700',
  high: 'bg-orange-100 text-orange-700',
  urgent: 'bg-red-100 text-red-700'
};

// Helper function to determine if a color is light or dark
const isLightColor = (hex: string): boolean => {
  const c = hex.replace('#', '');
  const r = parseInt(c.substring(0, 2), 16);
  const g = parseInt(c.substring(2, 4), 16);
  const b = parseInt(c.substring(4, 6), 16);
  const brightness = (r * 299 + g * 587 + b * 114) / 1000;
  return brightness > 155;
};

export default function TasksMeetingsQuickAccess({ userId, isActive = true }: TasksMeetingsQuickAccessProps) {
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const [activeTab, setActiveTab] = useState<'tasks' | 'meetings'>('tasks');
  const [tasks, setTasks] = useState<Task[]>([]);
  const [meetings, setMeetings] = useState<Meeting[]>([]);
  const [taskCount, setTaskCount] = useState(0);
  const [meetingCount, setMeetingCount] = useState(0);
  const [todayMeetingCount, setTodayMeetingCount] = useState(0);
  const [overdueTaskCount, setOverdueTaskCount] = useState(0);
  const [loading, setLoading] = useState(false);

  // Status change modal states
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [selectedItem, setSelectedItem] = useState<{ type: 'task' | 'meeting'; item: Task | Meeting } | null>(null);
  const [newStatus, setNewStatus] = useState('');
  const [statusComment, setStatusComment] = useState('');
  const [sendEmail, setSendEmail] = useState(false);
  const [updatingStatus, setUpdatingStatus] = useState(false);

  const dropdownRef = useRef<HTMLDivElement>(null);

  // Fetch data on component mount and when active
  useEffect(() => {
    if (isActive && userId) {
      fetchTasksAndMeetings();
      // Refresh data every 60 seconds
      const interval = setInterval(fetchTasksAndMeetings, 60000);
      return () => clearInterval(interval);
    }
  }, [isActive, userId]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const fetchTasksAndMeetings = async () => {
    setLoading(true);
    try {
      // Fetch tasks and meetings in parallel
      const [tasksRes, meetingsRes] = await Promise.all([
        fetch('/api/client-tasks/my-pending', { credentials: 'include' }),
        fetch('/api/meetings/my-pending', { credentials: 'include' })
      ]);

      if (tasksRes.ok) {
        const tasksData = await tasksRes.json();
        setTasks(tasksData.tasks || []);
        setTaskCount(tasksData.totalPending || 0);
        setOverdueTaskCount(tasksData.overdueCount || 0);
      }

      if (meetingsRes.ok) {
        const meetingsData = await meetingsRes.json();
        setMeetings(meetingsData.meetings || []);
        setMeetingCount(meetingsData.totalPending || 0);
        setTodayMeetingCount(meetingsData.todayMeetingsCount || 0);
      }
    } catch (error) {
      console.error('Error fetching tasks/meetings:', error);
    } finally {
      setLoading(false);
    }
  };

  const openStatusModal = (type: 'task' | 'meeting', item: Task | Meeting) => {
    setSelectedItem({ type, item });
    setNewStatus(item.status);
    setStatusComment('');
    setSendEmail(false);
    setShowStatusModal(true);
  };

  const handleStatusUpdate = async () => {
    if (!selectedItem || !newStatus) return;

    setUpdatingStatus(true);
    try {
      const { type, item } = selectedItem;

      if (type === 'task') {
        // Update task status
        const res = await fetch(`/api/client-tasks/${item._id}/status`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({
            status: newStatus,
            comment: statusComment
          })
        });

        if (!res.ok) {
          const data = await res.json();
          throw new Error(data.error || 'Failed to update task status');
        }

        const updatedTask = await res.json();

        // Send email if requested
        if (sendEmail && (item as Task).clientId?.email) {
          await sendStatusEmail('task', item as Task, newStatus, statusComment);
        }

        // Update local state
        if (newStatus === 'completed' || newStatus === 'cancelled') {
          setTasks(prev => prev.filter(t => t._id !== item._id));
          setTaskCount(prev => prev - 1);
        } else {
          setTasks(prev => prev.map(t => t._id === item._id ? { ...t, status: newStatus as any } : t));
        }
      } else {
        // Update meeting status
        const res = await fetch(`/api/meetings/${item._id}/status`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({
            status: newStatus,
            notes: statusComment
          })
        });

        if (!res.ok) {
          const data = await res.json();
          throw new Error(data.error || 'Failed to update meeting status');
        }

        // Send email if requested
        if (sendEmail && (item as Meeting).email) {
          await sendStatusEmail('meeting', item as Meeting, newStatus, statusComment);
        }

        // Update local state
        if (newStatus === 'completed' || newStatus === 'cancelled' || newStatus === 'no_show') {
          setMeetings(prev => prev.filter(m => m._id !== item._id));
          setMeetingCount(prev => prev - 1);
        } else {
          setMeetings(prev => prev.map(m => m._id === item._id ? { ...m, status: newStatus as any } : m));
        }
      }

      setShowStatusModal(false);
      setSelectedItem(null);
    } catch (error) {
      console.error('Error updating status:', error);
      alert(error instanceof Error ? error.message : 'Failed to update status');
    } finally {
      setUpdatingStatus(false);
    }
  };

  const sendStatusEmail = async (type: 'task' | 'meeting', item: Task | Meeting, status: string, comment: string) => {
    try {
      const isTask = type === 'task';
      const task = isTask ? item as Task : null;
      const meeting = isTask ? null : item as Meeting;

      const recipientEmail = isTask ? task?.clientId?.email : meeting?.email;
      const recipientName = isTask ? task?.clientId?.name : meeting?.contact_name;

      if (!recipientEmail) return;

      // Use centralized email template - pass all available data for consistent emails
      const { subject, html } = getQuickAccessStatusEmailTemplate({
        type,
        item: isTask
          ? {
            _id: task!._id,
            taskNumber: task!.taskNumber,
            title: task!.title,
            description: task!.description,
            createdAt: task!.createdAt,
            dueDate: task!.dueDate
          }
          : {
            _id: meeting!._id,
            subject: meeting!.subject,
            date: meeting!.date,
            start_time: meeting!.start_time,
            end_time: meeting!.end_time,
            meetingType: meeting!.meetingType,
            meeting_link: meeting!.meeting_link
          },
        clientName: recipientName || 'Valued Customer',
        clientEmail: recipientEmail,
        status,
        comment,
        senderName: 'REM Team'
      });

      const emailRes = await fetch('/api/auth/send-email', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          to: recipientEmail,
          subject,
          html
        })
      });

      if (emailRes.ok) {
        // Record the email
        const recordEndpoint = isTask
          ? `/api/client-tasks/${item._id}/email-record`
          : `/api/meetings/${item._id}/email-record`;

        await fetch(recordEndpoint, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          body: JSON.stringify({
            emailType: 'status_update',
            subject,
            recipient: recipientEmail,
            status: 'sent',
            relatedStatus: status,
            comment
          })
        });
      }
    } catch (error) {
      console.error('Error sending email:', error);
    }
  };

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const tomorrow = new Date(today);
    tomorrow.setDate(tomorrow.getDate() + 1);

    if (date >= today && date < tomorrow) {
      return 'Today';
    }

    const nextDay = new Date(tomorrow);
    nextDay.setDate(nextDay.getDate() + 1);
    if (date >= tomorrow && date < nextDay) {
      return 'Tomorrow';
    }

    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  const isOverdue = (dateStr: string) => {
    const date = new Date(dateStr);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return date < today;
  };

  const totalCount = taskCount + meetingCount;

  return (
    <div className="relative inline-flex items-center shrink-0" ref={dropdownRef}>
      {/* Task Icon Button */}
      <button
        type="button"
        onClick={() => { setActiveTab('tasks'); setIsOpen(!isOpen); }}
        className={`relative p-2 rounded-lg transition-colors shrink-0 ${activeTab === 'tasks' && isOpen ? 'bg-orange-100 text-orange-600' : 'text-slate-500 hover:bg-slate-100 hover:text-slate-700'}`}
        title={`Tasks (${taskCount} pending${overdueTaskCount > 0 ? `, ${overdueTaskCount} overdue` : ''})`}
      >
        <ClipboardIcon className="w-5 h-5" />
        {taskCount > 0 && (
          <div className={`absolute -top-1 -right-1 text-white text-xs rounded-full min-w-[18px] h-[18px] flex items-center justify-center font-bold shadow-lg ${overdueTaskCount > 0 ? 'bg-red-500' : 'bg-orange-500'}`}>
            {taskCount > 99 ? '99+' : taskCount}
          </div>
        )}
      </button>

      {/* Meeting Icon Button */}
      <button
        type="button"
        onClick={() => { setActiveTab('meetings'); setIsOpen(!isOpen); }}
        className={`relative p-2 rounded-lg transition-colors shrink-0 ${activeTab === 'meetings' && isOpen ? 'bg-purple-100 text-purple-600' : 'text-slate-500 hover:bg-slate-100 hover:text-slate-700'}`}
        title={`Meetings (${meetingCount} upcoming${todayMeetingCount > 0 ? `, ${todayMeetingCount} today` : ''})`}
      >
        <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" d="M15 10l4.553-2.276A1 1 0 0 1 21 8.618v6.764a1 1 0 0 1-1.447.894L15 14M5 18h8a2 2 0 0 0 2-2V8a2 2 0 0 0-2-2H5a2 2 0 0 0-2 2v8a2 2 0 0 0 2 2z" />
        </svg>
        {meetingCount > 0 && (
          <div className={`absolute -top-1 -right-1 text-white text-xs rounded-full min-w-[18px] h-[18px] flex items-center justify-center font-bold shadow-lg ${todayMeetingCount > 0 ? 'bg-red-500 animate-pulse' : 'bg-purple-500'}`}>
            {meetingCount > 99 ? '99+' : meetingCount}
          </div>
        )}
      </button>

      {/* Dropdown Panel */}
      {isOpen && (
        <div className="absolute right-0 top-full mt-2 w-96 bg-white rounded-xl shadow-2xl border border-slate-200 z-50 overflow-hidden">
          {/* Tabs */}
          <div className="flex border-b border-slate-200">
            <button
              onClick={() => setActiveTab('tasks')}
              className={`flex-1 py-3 px-4 text-sm font-medium transition-colors flex items-center justify-center gap-2 ${activeTab === 'tasks'
                ? 'text-orange-600 border-b-2 border-orange-500 bg-orange-50'
                : 'text-slate-500 hover:text-slate-700 hover:bg-slate-50'
                }`}
            >
              <ClipboardIcon className="w-4 h-4" />
              Tasks
              {taskCount > 0 && (
                <span className={`px-2 py-0.5 rounded-full text-xs font-bold ${overdueTaskCount > 0 ? 'bg-red-100 text-red-600' : 'bg-orange-100 text-orange-600'}`}>
                  {taskCount}
                </span>
              )}
            </button>
            <button
              onClick={() => setActiveTab('meetings')}
              className={`flex-1 py-3 px-4 text-sm font-medium transition-colors flex items-center justify-center gap-2 ${activeTab === 'meetings'
                ? 'text-purple-600 border-b-2 border-purple-500 bg-purple-50'
                : 'text-slate-500 hover:text-slate-700 hover:bg-slate-50'
                }`}
            >
              <CalendarIcon className="w-4 h-4" />
              Meetings
              {meetingCount > 0 && (
                <span className={`px-2 py-0.5 rounded-full text-xs font-bold ${todayMeetingCount > 0 ? 'bg-red-100 text-red-600' : 'bg-purple-100 text-purple-600'}`}>
                  {meetingCount}
                </span>
              )}
            </button>
          </div>

          {/* Content */}
          <div className="max-h-[400px] overflow-y-auto">
            {loading ? (
              <div className="py-8 text-center text-slate-500">
                <div className="animate-spin w-6 h-6 border-2 border-slate-300 border-t-slate-600 rounded-full mx-auto mb-2"></div>
                Loading...
              </div>
            ) : activeTab === 'tasks' ? (
              tasks.length === 0 ? (
                <div className="py-8 text-center text-slate-500">
                  <CheckCircleIcon className="w-10 h-10 mx-auto mb-2 text-green-400" />
                  <p className="font-medium">All caught up!</p>
                  <p className="text-sm">No pending tasks</p>
                </div>
              ) : (
                <div className="divide-y divide-slate-100">
                  {tasks.map(task => (
                    <div
                      key={task._id}
                      className="p-3 hover:bg-slate-50 transition-colors cursor-pointer"
                      onClick={() => {
                        setIsOpen(false);
                        if (task.clientId?._id) {
                          navigate(`/clients/${task.clientId._id}?tab=tasks`);
                        } else {
                          navigate(`/tasks?taskId=${task._id}`);
                        }
                      }}
                    >
                      <div className="flex items-start gap-3">
                        <div className={`w-2 h-2 rounded-full mt-2 flex-shrink-0 ${isOverdue(task.dueDate) ? 'bg-red-500' :
                          task.priority === 'urgent' ? 'bg-red-500' :
                            task.priority === 'high' ? 'bg-orange-500' :
                              task.priority === 'medium' ? 'bg-amber-500' : 'bg-slate-400'
                          }`} />
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <span className="text-xs text-slate-400 font-mono">{task.taskNumber}</span>
                            <span className={`px-1.5 py-0.5 text-xs rounded ${PRIORITY_COLORS[task.priority]}`}>
                              {task.priority}
                            </span>
                          </div>
                          <p className="font-medium text-slate-800 text-sm truncate">{task.title}</p>
                          {task.clientId?.name ? (
                            <p className="text-xs text-slate-500 truncate">{task.clientId.name}</p>
                          ) : task.categories && task.categories.length > 0 ? (
                            <div className="flex flex-wrap gap-1 mt-0.5">
                              {task.categories.slice(0, 2).map(cat => (
                                <span
                                  key={cat._id}
                                  className="inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium"
                                  style={{
                                    backgroundColor: cat.color,
                                    color: isLightColor(cat.color) ? '#1e293b' : '#ffffff'
                                  }}
                                >
                                  {cat.name}
                                </span>
                              ))}
                              {task.categories.length > 2 && (
                                <span className="text-xs text-slate-400">+{task.categories.length - 2}</span>
                              )}
                            </div>
                          ) : (
                            <p className="text-xs text-slate-400">No client</p>
                          )}
                          <div className="flex items-center gap-2 mt-1">
                            <span className={`text-xs ${isOverdue(task.dueDate) ? 'text-red-600 font-medium' : 'text-slate-400'}`}>
                              {isOverdue(task.dueDate) ? '⚠️ Overdue' : formatDate(task.dueDate)}
                            </span>
                            <span className={`px-1.5 py-0.5 text-xs rounded ${TASK_STATUS_OPTIONS.find(s => s.value === task.status)?.color || 'bg-slate-100 text-slate-600'
                              }`}>
                              {task.status.replace(/_/g, ' ')}
                            </span>
                          </div>
                        </div>
                        <button
                          onClick={(e) => { e.stopPropagation(); openStatusModal('task', task); }}
                          className="p-1.5 text-slate-400 hover:text-orange-600 hover:bg-orange-50 rounded transition-colors"
                          title="Change Status"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                          </svg>
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )
            ) : (
              meetings.length === 0 ? (
                <div className="py-8 text-center text-slate-500">
                  <CalendarIcon className="w-10 h-10 mx-auto mb-2 text-purple-400" />
                  <p className="font-medium">No upcoming meetings</p>
                  <p className="text-sm">Your schedule is clear</p>
                </div>
              ) : (
                <div className="divide-y divide-slate-100">
                  {meetings.map(meeting => {
                    const isToday = formatDate(meeting.date) === 'Today';
                    const linkedClientId = meeting.customFields?.linkedClientId;
                    return (
                      <div
                        key={meeting._id}
                        className="p-3 hover:bg-slate-50 transition-colors cursor-pointer"
                        onClick={() => {
                          setIsOpen(false);
                          // If support meeting with linked client, go to client page with meetings tab
                          if (meeting.meetingCategory === 'support' && linkedClientId) {
                            navigate(`/clients/${linkedClientId}?tab=meetings`);
                          } else {
                            navigate(`/meetings/${meeting._id}`);
                          }
                        }}
                      >
                        <div className="flex items-start gap-3">
                          <div className={`w-2 h-2 rounded-full mt-2 flex-shrink-0 ${isToday ? 'bg-red-500 animate-pulse' : 'bg-purple-500'}`} />
                          <div className="flex-1 min-w-0">
                            <p className="font-medium text-slate-800 text-sm truncate">{meeting.subject}</p>
                            <p className="text-xs text-slate-500">{meeting.contact_name} {meeting.company_name && `• ${meeting.company_name}`}</p>
                            <div className="flex items-center gap-2 mt-1 flex-wrap">
                              <span className={`text-xs font-medium ${isToday ? 'text-red-600' : 'text-slate-500'}`}>
                                {formatDate(meeting.date)} at {meeting.start_time}
                              </span>
                              <span className={`px-1.5 py-0.5 text-xs rounded ${meeting.meetingType === 'online' ? 'bg-blue-100 text-blue-600' : 'bg-amber-100 text-amber-600'
                                }`}>
                                {meeting.meetingType === 'online' ? '🖥️ Online' : '📍 On-site'}
                              </span>
                              <span className={`px-1.5 py-0.5 text-xs rounded ${MEETING_STATUS_OPTIONS.find(s => s.value === meeting.status)?.color || 'bg-slate-100 text-slate-600'
                                }`}>
                                {meeting.status}
                              </span>
                            </div>
                          </div>
                          <button
                            onClick={(e) => { e.stopPropagation(); openStatusModal('meeting', meeting); }}
                            className="p-1.5 text-slate-400 hover:text-purple-600 hover:bg-purple-50 rounded transition-colors"
                            title="Change Status"
                          >
                            <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                            </svg>
                          </button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )
            )}
          </div>

          {/* Footer - View All */}
          <div className="border-t border-slate-200 p-2 bg-slate-50">
            <button
              onClick={() => {
                setIsOpen(false);
                navigate('/calendar');
              }}
              className={`block w-full text-center py-2 text-sm font-medium rounded-lg transition-colors ${activeTab === 'tasks'
                ? 'text-orange-600 hover:bg-orange-100'
                : 'text-purple-600 hover:bg-purple-100'
                }`}
            >
              View All {activeTab === 'tasks' ? 'Tasks' : 'Meetings'} →
            </button>
          </div>
        </div>
      )}

      {/* Status Change Modal */}
      {showStatusModal && selectedItem && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[100]" onClick={() => setShowStatusModal(false)}>
          <div
            className="bg-white rounded-xl shadow-2xl w-full max-w-md mx-4 overflow-hidden"
            onClick={e => e.stopPropagation()}
          >
            {/* Modal Header */}
            <div className={`px-5 py-4 ${selectedItem.type === 'task' ? 'bg-gradient-to-r from-orange-500 to-amber-500' : 'bg-gradient-to-r from-purple-500 to-indigo-500'}`}>
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold text-white">
                  Update {selectedItem.type === 'task' ? 'Task' : 'Meeting'} Status
                </h3>
                <button
                  onClick={() => setShowStatusModal(false)}
                  className="p-1 rounded-full hover:bg-white/20 transition-colors"
                >
                  <XIcon className="w-5 h-5 text-white" />
                </button>
              </div>
              <p className="text-white/80 text-sm mt-1 truncate">
                {selectedItem.type === 'task'
                  ? (selectedItem.item as Task).title
                  : (selectedItem.item as Meeting).subject}
              </p>
            </div>

            {/* Modal Body */}
            <div className="p-5 space-y-4">
              {/* Status Selection */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">New Status</label>
                <div className="grid grid-cols-2 gap-2">
                  {(selectedItem.type === 'task' ? TASK_STATUS_OPTIONS : MEETING_STATUS_OPTIONS).map(option => (
                    <button
                      key={option.value}
                      onClick={() => setNewStatus(option.value)}
                      className={`px-3 py-2 rounded-lg text-sm font-medium border-2 transition-all ${newStatus === option.value
                        ? `${option.color} border-current ring-2 ring-offset-1 ${selectedItem.type === 'task' ? 'ring-orange-300' : 'ring-purple-300'}`
                        : 'border-slate-200 text-slate-600 hover:border-slate-300'
                        }`}
                    >
                      {option.label}
                    </button>
                  ))}
                </div>
              </div>

              {/* Comment Input */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">
                  {selectedItem.type === 'meeting' ? 'Notes (required)' : 'Comment (optional)'}
                  {selectedItem.type === 'meeting' && <span className="text-red-500 ml-1">*</span>}
                </label>
                <textarea
                  value={statusComment}
                  onChange={(e) => setStatusComment(e.target.value)}
                  placeholder={selectedItem.type === 'meeting'
                    ? 'Add notes about this status change... (required)'
                    : 'Add a comment about this status change...'}
                  className={`w-full px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-offset-1 focus:border-transparent resize-none ${selectedItem.type === 'meeting' && !statusComment.trim() ? 'border-red-300' : 'border-slate-300'}`}
                  style={{
                    focusRing: selectedItem.type === 'task' ? 'ring-orange-300' : 'ring-purple-300'
                  } as any}
                  rows={3}
                />
                {selectedItem.type === 'meeting' && !statusComment.trim() && (
                  <p className="text-red-500 text-xs mt-1">Notes are required when changing meeting status</p>
                )}
              </div>

              {/* Send Email Checkbox */}
              {((selectedItem.type === 'task' && (selectedItem.item as Task).clientId?.email) ||
                (selectedItem.type === 'meeting' && (selectedItem.item as Meeting).email)) && (
                  <label className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg cursor-pointer hover:bg-slate-100 transition-colors">
                    <input
                      type="checkbox"
                      checked={sendEmail}
                      onChange={(e) => setSendEmail(e.target.checked)}
                      className={`w-4 h-4 rounded ${selectedItem.type === 'task' ? 'text-orange-500' : 'text-purple-500'}`}
                    />
                    <div className="flex-1">
                      <span className="text-sm font-medium text-slate-700">Send email notification</span>
                      <p className="text-xs text-slate-500">
                        Notify {selectedItem.type === 'task'
                          ? (selectedItem.item as Task).clientId?.email
                          : (selectedItem.item as Meeting).email}
                      </p>
                    </div>
                    <PaperAirplaneIcon className="w-4 h-4 text-slate-400" />
                  </label>
                )}
            </div>

            {/* Modal Footer */}
            <div className="px-5 py-4 bg-slate-50 border-t border-slate-200 flex items-center justify-end gap-3">
              <button
                onClick={() => setShowStatusModal(false)}
                className="px-4 py-2 text-sm font-medium text-slate-600 hover:text-slate-800 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleStatusUpdate}
                disabled={updatingStatus || newStatus === selectedItem.item.status || (selectedItem.type === 'meeting' && !statusComment.trim())}
                className={`px-5 py-2 text-sm font-medium text-white rounded-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed ${selectedItem.type === 'task'
                  ? 'bg-gradient-to-r from-orange-500 to-amber-500 hover:from-orange-600 hover:to-amber-600'
                  : 'bg-gradient-to-r from-purple-500 to-indigo-500 hover:from-purple-600 hover:to-indigo-600'
                  }`}
              >
                {updatingStatus ? (
                  <span className="flex items-center gap-2">
                    <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    Updating...
                  </span>
                ) : (
                  'Update Status'
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

